import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, Mail } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildPageMeta } from "@/lib/seoIndex";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  useSEO({ robots: decideRobots({ pageType: "auth" }), ...buildPageMeta("auth") });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="forgot-password-page">
      <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-[#5F6B61] hover:text-[#5B8C66] mb-6">
        <ArrowLeft size={14}/> {t("login.submit")}
      </Link>
      <p className="eyebrow">{t("forgot.eyebrow")}</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">{t("forgot.title")}</h1>
      <p className="mt-2 text-sm text-[#5F6B61]">{t("forgot.subtitle")}</p>

      {sent ? (
        <div className="mt-8 card-clay p-7 text-center" data-testid="forgot-success">
          <Mail size={40} className="mx-auto text-[#5B8C66]" strokeWidth={1.4}/>
          <h3 className="mt-4 text-lg font-bold">{t("forgot.success_title")}</h3>
          <p className="mt-2 text-sm text-[#5F6B61]">{t("forgot.success_body")}</p>
          <Link to="/login" className="btn-pill mt-6 inline-block px-5 py-2.5 bg-[#5B8C66] text-white font-semibold">{t("login.submit")}</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4 card-clay p-7" data-testid="forgot-form">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#3F6147] mb-2 block">{t("login.email")}</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-[#DED7C7] focus:outline-none focus:ring-2 focus:ring-[#5B8C66] text-sm bg-white"
              data-testid="forgot-email"
            />
          </div>
          {error && <div className="text-sm text-[#3F6147] bg-[#E9F0E0] border border-[#5B8C66]/20 rounded-xl px-4 py-3" data-testid="forgot-error">{error}</div>}
          <button type="submit" disabled={loading} className="btn-pill w-full px-5 py-3 bg-[#5B8C66] text-white font-bold hover:bg-[#3F6147] transition disabled:opacity-60" data-testid="forgot-submit">
            {loading ? t("forgot.submitting") : t("forgot.submit")}
          </button>
        </form>
      )}
    </div>
  );
}
